import {
  getMcpOAuthErrorKind,
  isExpectedMcpOAuthFailure,
} from "../utils/mcpOAuth.js";

const REQUEST_FAILED_MESSAGE = "MCP OAuth request failed";

const isAborted = (error) =>
  error?.name === "AbortError" || error?.code === "ERR_CANCELED";

const isNetworkFailure = (error) =>
  !error?.response &&
  (error?.code === "ERR_NETWORK" || error?.code === "ECONNABORTED");

/**
 * Safe LaunchError arguments. Status and function name only.
 * Query params, authorization codes, verifiers, tokens, and keys stay off
 * the object LaunchError logs and sends.
 */
export const buildMcpOAuthLaunchErrorArgs = ({
  status = null,
  functionName,
}) => {
  const error = new Error(REQUEST_FAILED_MESSAGE);

  return {
    error,
    showAlert: false,
    ignoreAlertByErrors: [],
    sentryMeta: {
      functionName,
      skipSentry: false,
      context: {
        status: typeof status === "number" ? status : null,
      },
      tags: { operation: "mcp_oauth" },
    },
  };
};

const readResponseCode = (payload) =>
  typeof payload?.code === "string" ? payload.code : "";

export const requestMcpEndpoint = async ({
  dispatch,
  launchError,
  axiosClient,
  method,
  url,
  body,
  authToken,
  functionName,
}) => {
  const reportUnexpected = ({ status }) => {
    const { error, showAlert, ignoreAlertByErrors, sentryMeta } =
      buildMcpOAuthLaunchErrorArgs({ status, functionName });
    return dispatch(
      launchError(error, showAlert, ignoreAlertByErrors, sentryMeta)
    );
  };

  try {
    const headers = {};
    if (authToken) headers.Authorization = `Bearer ${authToken}`;

    const response = await axiosClient({
      method,
      url,
      data: body,
      headers,
    });
    const data = response?.data;
    const status = response?.status || 200;

    if (data?.ok === true) {
      return { ok: true, data: data.data, status };
    }

    const code = readResponseCode(data);
    if (!isExpectedMcpOAuthFailure({ status, code, isNetwork: false })) {
      await reportUnexpected({ status });
    }

    return {
      ok: false,
      code,
      errorKind: getMcpOAuthErrorKind({ status, code }),
      status,
    };
  } catch (error) {
    if (isAborted(error)) {
      return { ok: false, code: "", errorKind: "unexpected" };
    }

    const status = error?.response?.status;
    const code = readResponseCode(error?.response?.data);
    const isNetwork = isNetworkFailure(error);

    if (!isExpectedMcpOAuthFailure({ status, code, isNetwork })) {
      await reportUnexpected({
        status: typeof status === "number" ? status : null,
      });
    }

    return {
      ok: false,
      code,
      errorKind: getMcpOAuthErrorKind({ status, code, isNetwork }),
      status: status ?? null,
    };
  }
};
